import * as React from 'react';

import { ExclamationCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { Row, Spin } from 'antd';
import { useAppDispatch, useAppSelector } from 'src/types/reduxHelpers';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { PageWrapper } from 'src/components/pageWrapper';
import { getEmployeeFromInvitationToken } from 'src/store/modules/invitationModule';
import { useApiErrorMessage } from 'src/hooks/useApiErrorMessage';
import { useAppRoutes } from 'src/hooks';
import { useNonIdealState } from 'src/hooks/useNonIdealState';
import { useTranslation } from 'react-i18next';

interface HomePageProps {

}


export const HomePage: React.FC<HomePageProps> = (props) => {
    const { t } = useTranslation();
    const { getErrorMessage } = useApiErrorMessage();

    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();

    const nonIdealState = useNonIdealState();

    const [isLoading, setIsLoading] = React.useState(false);
    const [isMounted, setIsMounted] = React.useState(false);

    const savedToken = useAppSelector((state) => state.invitation.token);

    const appRoutes = useAppRoutes();
    const navigate = useNavigate();

    React.useEffect(() => {
        setIsMounted(true);

        return () => setIsMounted(false);
    }, []);

    React.useEffect(() => {
        const loadAsync = async (token: string): Promise<void> => {
            setIsLoading(true);

            const action = await dispatch(getEmployeeFromInvitationToken(token));

            if (getEmployeeFromInvitationToken.rejected.match(action)) {
                nonIdealState.setNonIdealState({
                    isNonIdeal: true,
                    header: t('Common.error'),
                    subheader: getErrorMessage(action.payload),
                    icon: <ExclamationCircleOutlined />,
                });

                setIsLoading(false);

                return;
            }

            navigate(appRoutes.intro.path, { replace: true });

            if (isMounted) {
                setIsLoading(false);
            }
        };

        // Get the token from our query params
        const token = searchParams.get('token') ?? savedToken;

        if (!token) {
            // We don't have a token in the URL, and no employee has been set: Block
            nonIdealState.setNonIdealState({
                header: t('Pages.HomePage.no_token_header'),
                subheader: t('Pages.HomePage.no_token_subheader'),
                isNonIdeal: true,
                icon: <LinkOutlined />,
            });

            return;
        }

        loadAsync(token);
    }, []);

    return (
        <PageWrapper
            isNonIdeal={nonIdealState.isNonIdeal}
            nonIdealActions={nonIdealState.actions}
            nonIdealHeader={nonIdealState.header}
            nonIdealSubheader={nonIdealState.subheader}
            nonIdealIconType={nonIdealState.icon}
        >
            <Row
                align="middle"
                justify="center"
                gutter={20}
            >
                {isLoading &&
                    <Spin
                        style={{ margin: 20, transform: 'scale(2)' }}
                    />
                }
            </Row>
        </PageWrapper>
    );
};